import React, { Fragment, useCallback, useMemo, useRef, useState, useEffect } from 'react'
import Text from 'core/elements/Text'
import TextField from 'core/components/validatedForm/TextField'
import { remove, update, uniq } from 'ramda'
import { makeStyles } from '@material-ui/styles'
import Theme from 'core/themes/model'
import Button from 'core/elements/button'
import CheckboxField from 'core/components/validatedForm/CheckboxField'
import FontAwesomeIcon from 'core/components/FontAwesomeIcon'
import { middleLeft } from 'core/elements/menu/defaults'
import useListAction from 'core/hooks/useListAction'
import { useSelector } from 'react-redux'
import { listResmgrHosts } from '../../actions'
import { resmgrHostsSelector } from '../../selectors'
import { customValidator } from 'core/utils/fieldValidators'
import clsx from 'clsx'
import Alert from 'core/components/Alert'
import ToggleSwitch from 'core/elements/ToggleSwitch'
import DropdownField from 'core/components/validatedForm/DropdownField'
import Dropdown from 'core/elements/dropdown'
import { getGpuModels } from './helpers'
import HintTooltip from 'core/elements/tooltip/HintTooltip'
import Tooltip from 'core/elements/tooltip'
import { trackEvent } from 'utils/tracking'

const useStyles = makeStyles<Theme>((theme) => ({
  table: {
    display: 'grid',
    gridTemplateColumns: 'auto auto max-content max-content max-content max-content max-content',
    alignItems: 'center',
  },
  withDelete: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  sameLine: {
    display: 'grid',
    gap: 8,
    alignItems: 'center',
    gridTemplateColumns: 'max-content max-content auto',
  },
  error: {
    marginLeft: 16,
    color: theme.components.badge.error.color,
  },
  errorText: {
    color: theme.components.badge.error.color,
  },
  sameLineCell: {
    display: 'grid',
    gap: 8,
    alignItems: 'center',
    gridTemplateColumns: 'max-content auto',
  },
  emptyHeader: {
    borderBottom: `1px solid ${theme.components.table.border}`,
    padding: '4px 0px',
    height: '100%',
  },
  header: {
    borderBottom: `1px solid ${theme.components.table.border}`,
    padding: '4px 8px',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  },
  cell: {
    padding: '8px 8px 16px',
  },
  remove: {
    color: theme.components.badge.primary.color,
    cursor: 'pointer',
  },
  disabled: {
    color: theme.components.button.primary.disabledBackground,
  },
  alert: {
    padding: theme.spacing(1),
  },
  gpuConfig: {
    display: 'grid',
    gap: theme.spacing(4),
    alignItems: 'start',
    gridTemplateColumns: 'max-content auto auto',
  },
  gpuToggle: {
    marginTop: theme.spacing(1),
  },
  srIovDeviceCheckbox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: theme.spacing(1),
    marginTop: theme.spacing(0.5),
  },
  gpuPciPassthroughsDropdown: {
    minWidth: 400,
  },
}))

export const defaultInterfaceConfig = {
  physnet: '',
  physnetName: '',
  management: false,
  tunnel: false,
  hostLiveness: false,
  imageLibrary: false,
  vmConsole: false,
  physicalNetworkLabel: '',
  GpuPciPassthroughs: [],
}

const virtualNetworkLabelValidator = customValidator((label, params) => {
  // Technically don't need to test the label variable itself
  const allConfigs = params.hostNetworkConfigs
  const virtualNetworkLabels = []
  for (const config of allConfigs) {
    for (const iface of config.networkInterfaces) {
      if (iface.tunnel) {
        virtualNetworkLabels.push(iface.physicalNetworkLabel)
      }
    }
  }
  const uniqueLabels = uniq(virtualNetworkLabels)
  return uniqueLabels.length === 1
}, `The physical network label for the network interface serving virtual network tunnels must be the same across all Host Network Configurations`)

const validLabelString = customValidator((label) => {
  if (!label) {
    return true
  }
  return /^[a-zA-Z0-9_-]+$/i.test(label)
}, 'Physical network label must only consist of alphanumeric characters, hyphens(-), and underscores(_)')

export default function HostNetworkConfiguration({
  params,
  updateParams,
  config,
  idx,
  existingHostNetworkConfig,
}) {
  const classes = useStyles()
  const { message, loading, reload } = useListAction(listResmgrHosts, {
    params,
  })
  const [gpuModels, setGpuModels] = useState([])
  const hosts = useSelector(resmgrHostsSelector)
  const usedHostConfigIds = hosts?.map((host) => host?.hostconfig_id)?.filter((id) => !!id)
  const [enableGpuPassthrough, setEnableGpuPassthrough] = useState<boolean>(
    !!config?.gpuPciPassthroughs?.length,
  )

  useEffect(() => {
    const makeCalls = async () => {
      const models = await getGpuModels()
      setGpuModels(models)
    }
    makeCalls()
  }, [])

  const updateField = useCallback(
    (value, prop) => {
      const updatedConfig = {
        ...config,
        [prop]: value,
      }
      const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
      updateParams({ hostNetworkConfigs: updatedConfigs })
    },
    [params.hostNetworkConfigs, config, idx],
  )

  const deleteConfig = useCallback(() => {
    const updatedConfigs = remove(idx, 1, params.hostNetworkConfigs)
    updateParams({ hostNetworkConfigs: updatedConfigs })
    trackEvent(`Cluster Blueprint - Clicked Remove Host Network Configuration`)
  }, [params.hostNetworkConfigs, idx])

  const addNetworkInterface = useCallback(() => {
    const updatedConfig = {
      ...config,
      networkInterfaces: [...config.networkInterfaces, defaultInterfaceConfig],
    }
    const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
    updateParams({ hostNetworkConfigs: updatedConfigs })
    trackEvent(`Cluster Blueprint - Clicked Add Network Interface`)
  }, [params.hostNetworkConfigs, config, idx])

  const updateNetworkInterface = useCallback(
    (value, property, interfaceIndex) => {
      const currentInterface = config?.networkInterfaces?.[interfaceIndex]
      const updatedInterface = {
        ...currentInterface,
        [property]: value,
      }
      const updatedConfig = {
        ...config,
        networkInterfaces: update(interfaceIndex, updatedInterface, config?.networkInterfaces),
      }
      const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
      updateParams({ hostNetworkConfigs: updatedConfigs })
    },
    [params.hostNetworkConfigs, config, idx],
  )

  const removeNetworkInterface = useCallback(
    (interfaceIndex) => {
      const updatedConfig = {
        ...config,
        networkInterfaces: remove(interfaceIndex, 1, config?.networkInterfaces),
      }
      const updatedConfigs = update(idx, updatedConfig, params.hostNetworkConfigs)
      updateParams({ hostNetworkConfigs: updatedConfigs })
      trackEvent(`Cluster Blueprint - Clicked Remove Network Interface`)
    },
    [params.hostNetworkConfigs, config, idx],
  )

  const trafficAssigned = useCallback(
    (property) => {
      const assigned = config.networkInterfaces?.some((iface) => !!iface[property])
      return assigned
    },
    [config],
  )

  const physicalNetworkLabelsAdded = useMemo(
    () => config?.networkInterfaces.some((iface) => !!iface?.physicalNetworkLabel),
    [config],
  )

  const newPhysicalNetworkLabelsAdded = useMemo(
    () => config?.networkInterfaces.some((iface) => iface?.physnetName === ''),
    [config],
  )

  const allTrafficAssigned =
    trafficAssigned('management') &&
    trafficAssigned('vmConsole') &&
    trafficAssigned('imageLibrary') &&
    trafficAssigned('tunnel') &&
    trafficAssigned('hostLiveness')

  // For some reason when removing one of the configs, params
  // in second arg of customValidator is messed up, so use useMemo here
  const uniqueHostConfigNameValidator = useMemo(
    () =>
      customValidator((name) => {
        const matchingConfigNames = params.hostNetworkConfigs
          ?.map((config) => config.name)
          ?.filter((configName) => configName === name)
        return matchingConfigNames.length < 2
      }, 'Each host network configuration must have a unique name'),
    [params],
  )

  const getUniqueLabelValidator = useCallback(
    (configIndex) =>
      customValidator((label) => {
        const config = params.hostNetworkConfigs?.[configIndex]
        const labels = config.networkInterfaces
          .map((iface) => iface.physicalNetworkLabel)
          ?.filter((label) => !!label)
        const uniqueLabels = uniq(labels)
        return labels.length === uniqueLabels.length
      }, 'Labels must be unique per network interface on a Host Network Configuration'),
    [params.hostNetworkConfigs],
  )

  const isUsedHostConfig = useMemo(
    () => usedHostConfigIds?.length > 0 && usedHostConfigIds.includes(config?.id),
    [config, usedHostConfigIds],
  )

  const passthroughList = useMemo(
    () =>
      gpuModels.map((passthrough) => ({
        label: passthrough.model,
        value: passthrough.name,
      })),
    [gpuModels],
  )

  // Check if there is an existing GPU host config
  // If yes, check if the current config has any GPU passthroughs and allow it
  // const isNotAnExistingGPUHostConfig = useMemo(
  //   () =>
  //     params?.hostNetworkConfigs?.some((config) => !!config?.gpuPciPassthroughs?.length) &&
  //     (!config?.gpuPciPassthroughs ||
  //       config?.gpuPciPassthroughs?.length === 0 ||
  //       !enableGpuPassthrough),
  //   [params?.hostNetworkConfigs, enableGpuPassthrough, config?.gpuPciPassthroughs],
  // )

  return (
    <div>
      <div className={classes.withDelete}>
        <div className={classes.sameLine}>
          <Text variant="caption1">Name this configuration:</Text>
          <TextField
            id={`hostNetworkConfigs.${idx}.name`}
            label=""
            onChange={(value) => updateField(value, 'name')}
            value={config?.name}
            placeholder="Name"
            validations={[uniqueHostConfigNameValidator]}
            required
          />
          {!physicalNetworkLabelsAdded && allTrafficAssigned && (
            <div className={classes.sameLine}>
              <FontAwesomeIcon className={classes.error}>circle-exclamation</FontAwesomeIcon>
              <Text variant="body2" className={classes.errorText}>
                At least one physical network label name should be provided
              </Text>
            </div>
          )}
          {!allTrafficAssigned && (
            <div className={classes.sameLine}>
              <FontAwesomeIcon className={classes.error}>circle-exclamation</FontAwesomeIcon>
              <Text variant="body2" className={classes.errorText}>
                All system traffic types must be assigned
              </Text>
            </div>
          )}
        </div>
        <Tooltip
          message={isUsedHostConfig ? 'This configuration is in-use and cannot be deleted' : ''}
        >
          <Button
            type="button"
            variant="tertiary"
            disabled={isUsedHostConfig}
            onClick={() => deleteConfig()}
          >
            Delete Configuration
          </Button>
        </Tooltip>
      </div>
      <div className={classes.table}>
        <div className={classes.header}>
          <Text variant="caption1">Network Interface</Text>
        </div>
        <div className={classes.header}>
          <Text variant="caption1">Physical Network Label</Text>
        </div>
        <div className={classes.header}>
          <Text variant="caption1">Management</Text>
        </div>
        <div className={classes.header}>
          <Text variant="caption1">VM Console</Text>
        </div>
        <div className={classes.header}>
          <Text variant="caption1">
            Image
            <br />
            Library I/O
          </Text>
        </div>
        <div className={classes.header}>
          <Text variant="caption1">
            Virtual Network
            <br />
            Tunnels
          </Text>
        </div>
        <div className={classes.header}>
          <Text variant="caption1">
            Host Liveness
            <br />
            Checks
          </Text>
        </div>
        {config?.networkInterfaces?.map((iface, ifaceIdx) => (
          <Fragment key={ifaceIdx}>
            <div className={classes.cell}>
              <div className={classes.sameLineCell}>
                <FontAwesomeIcon
                  className={clsx(
                    classes.remove,
                    isUsedHostConfig && iface?.physnetName !== '' && classes.disabled,
                  )}
                  onClick={() => removeNetworkInterface(ifaceIdx)}
                  disabled={isUsedHostConfig && iface?.physnetName !== ''}
                  solid
                >
                  circle-minus
                </FontAwesomeIcon>
                <TextField
                  id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.physnet`}
                  label=""
                  onChange={(value) => {
                    // Do not allow spaces
                    const strippedValue = value.replace(/\s/g, '')
                    updateNetworkInterface(strippedValue, 'physnet', ifaceIdx)
                  }}
                  value={iface.physnet}
                  placeholder="Physical Interface Name"
                  disabled={isUsedHostConfig && iface?.physnetName !== ''}
                  required
                />
              </div>
            </div>
            <div className={classes.cell}>
              <TextField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.physicalNetworkLabel`}
                label=""
                onChange={(value) => {
                  // Do not allow spaces
                  const strippedValue = value.replace(/\s/g, '')
                  updateNetworkInterface(strippedValue, 'physicalNetworkLabel', ifaceIdx)
                }}
                value={iface.physicalNetworkLabel}
                placeholder={
                  (iface.tunnel && params.segmentationTechnology === 'vlan') ||
                  (isUsedHostConfig && iface?.physnetName === '')
                    ? 'Label (required)'
                    : 'Label (optional)'
                }
                disabled={isUsedHostConfig && iface?.physnetName !== ''}
                required={
                  (iface.tunnel && params.segmentationTechnology === 'vlan') ||
                  (isUsedHostConfig && iface?.physnetName === '')
                }
                validations={
                  // If the host config is already in use and existing label, don't validate the label
                  isUsedHostConfig && iface?.physnetName !== ''
                    ? null
                    : iface.tunnel && params.segmentationTechnology === 'vlan'
                    ? [virtualNetworkLabelValidator, validLabelString, getUniqueLabelValidator(idx)]
                    : [validLabelString, getUniqueLabelValidator(idx)]
                }
              />
            </div>
            <div className={classes.cell}>
              <CheckboxField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.management`}
                label=""
                value={iface.management}
                onChange={(value) => updateNetworkInterface(value, 'management', ifaceIdx)}
                disabled={
                  (isUsedHostConfig && iface?.physnetName !== '') ||
                  (trafficAssigned('management') && !iface?.management)
                }
                info={
                  trafficAssigned('management') && !iface?.management
                    ? 'Another interface has already been assigned management traffic'
                    : null
                }
                type="checkbox"
                tooltipAlign={middleLeft.align}
                tooltipOffset={middleLeft.offset}
              />
            </div>
            <div className={classes.cell}>
              <CheckboxField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.vmConsole`}
                label=""
                value={iface.vmConsole}
                onChange={(value) => updateNetworkInterface(value, 'vmConsole', ifaceIdx)}
                disabled={
                  (isUsedHostConfig && iface?.physnetName !== '') ||
                  (trafficAssigned('vmConsole') && !iface?.vmConsole)
                }
                info={
                  trafficAssigned('vmConsole') && !iface?.vmConsole
                    ? 'Another interface has already been assigned VM console traffic'
                    : null
                }
                type="checkbox"
                tooltipAlign={middleLeft.align}
                tooltipOffset={middleLeft.offset}
              />
            </div>
            <div className={classes.cell}>
              <CheckboxField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.imageLibrary`}
                label=""
                value={iface.imageLibrary}
                onChange={(value) => updateNetworkInterface(value, 'imageLibrary', ifaceIdx)}
                disabled={
                  (isUsedHostConfig && iface?.physnetName !== '') ||
                  (trafficAssigned('imageLibrary') && !iface?.imageLibrary)
                }
                info={
                  trafficAssigned('imageLibrary') && !iface?.imageLibrary
                    ? 'Another interface has already been assigned image library traffic'
                    : null
                }
                type="checkbox"
                tooltipAlign={middleLeft.align}
                tooltipOffset={middleLeft.offset}
              />
            </div>
            <div className={classes.cell}>
              <CheckboxField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.tunnel`}
                label=""
                value={iface.tunnel}
                onChange={(value) => updateNetworkInterface(value, 'tunnel', ifaceIdx)}
                disabled={
                  (isUsedHostConfig && iface?.physnetName !== '') ||
                  (trafficAssigned('tunnel') && !iface?.tunnel)
                }
                info={
                  trafficAssigned('tunnel') && !iface?.tunnel
                    ? 'Another interface has already been assigned virtual network tunnel traffic'
                    : null
                }
                type="checkbox"
                tooltipAlign={middleLeft.align}
                tooltipOffset={middleLeft.offset}
              />
            </div>
            <div className={classes.cell}>
              <CheckboxField
                id={`hostNetworkConfigs.${idx}.networkInterfaces.${ifaceIdx}.hostLiveness`}
                label=""
                value={iface.hostLiveness}
                onChange={(value) => updateNetworkInterface(value, 'hostLiveness', ifaceIdx)}
                disabled={
                  (isUsedHostConfig && iface?.physnetName !== '') ||
                  (trafficAssigned('hostLiveness') && !iface?.hostLiveness)
                }
                info={
                  trafficAssigned('hostLiveness') && !iface?.hostLiveness
                    ? 'Another interface has already been assigned for host liveness checks'
                    : null
                }
                type="checkbox"
                tooltipAlign={middleLeft.align}
                tooltipOffset={middleLeft.offset}
              />
            </div>
          </Fragment>
        ))}
      </div>
      {isUsedHostConfig && newPhysicalNetworkLabelsAdded && (
        <div className={classes.alert}>
          <Alert
            variant="warning"
            message={
              <>
                Adding a new NIC here will also propagate to already configured hosts using this
                Host Network Configuration. To avoid any disruption to active workloads, please
                ensure that this NIC is present on all such hosts.
              </>
            }
          />
        </div>
      )}
      <div className={classes.cell}>
        <Button type="button" icon="plus" onClick={addNetworkInterface}>
          Add Network Interface
        </Button>
      </div>

      <div className={classes.gpuConfig}>
        <Tooltip
          message={
            isUsedHostConfig && existingHostNetworkConfig?.gpuPciPassthroughs?.length > 0
              ? 'Cannot disable GPU passthrough mode if host config is already in use'
              : ''
          }
        >
          <ToggleSwitch
            label="Enable GPU Passthrough"
            onClick={(value) => {
              if (!value) {
                updateField([], 'gpuPciPassthroughs')
              }
              setEnableGpuPassthrough(value)
            }}
            active={enableGpuPassthrough}
            className={classes.gpuToggle}
            disabled={isUsedHostConfig && existingHostNetworkConfig?.gpuPciPassthroughs?.length > 0}
          />
        </Tooltip>
        {enableGpuPassthrough && (
          <>
            <DropdownField
              DropdownComponent={Dropdown}
              id={`hostNetworkConfigs.${idx}.gpuPciPassthroughs`}
              className={classes.gpuPciPassthroughsDropdown}
              tooltip={'Select a GPU model supported by this configuration'}
              onChange={(value) => {
                updateField([value], 'gpuPciPassthroughs')
              }}
              value={config?.gpuPciPassthroughs?.[0]}
              data-testid="passthroughs-multi-select"
              items={passthroughList}
              disabled={
                isUsedHostConfig && existingHostNetworkConfig?.gpuPciPassthroughs?.length > 0
              }
              required
            />
            <div className={classes.srIovDeviceCheckbox}>
              <CheckboxField
                label="SR-IOV Device"
                id={`hostNetworkConfigs.${idx}.gpuPciPassthroughs`}
                onChange={(value) => {
                  updateField(value, 'srIOVDevice')
                }}
                value={!!config?.srIOVDevice}
                disabled={
                  isUsedHostConfig && existingHostNetworkConfig?.gpuPciPassthroughs?.length > 0
                }
              />
              <HintTooltip message="Enable this if the GPU device supports SR-IOV." />
            </div>
          </>
          // TODO - MultiSelect not supported for JUNE release
          // <PassthroughMultiSelect
          //   id={`hostNetworkConfigs.${idx}.gpuPciPassthroughs`}
          //   tooltip={'Select GPU models supported by this configuration'}
          //   onChange={(value) => {
          //     // Do not allow removing GPU passthroughs if the host network config is already in use
          //     if (
          //       existingHostNetworkConfig?.gpuPciPassthroughs?.length &&
          //       (value.length < existingHostNetworkConfig?.gpuPciPassthroughs?.length ||
          //         !existingHostNetworkConfig?.gpuPciPassthroughs?.every((item) =>
          //           value.includes(item),
          //         ))
          //     ) {
          //       return
          //     }
          //     updateField(value, 'gpuPciPassthroughs')
          //   }}
          //   value={config?.gpuPciPassthroughs}
          //   data-testid="passthroughs-multi-select"
          //   maxHeight={165}
          // />
        )}
      </div>
    </div>
  )
}
